import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { X, Download, FileText, AlertCircle, Loader2, Printer } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/** Kompakte Dokumentvorschau (PDF/Bilder) mit Download & Drucken – für Hausakte, Vorgangs-Anlagen, Quittungen & Co.
 *  Entweder `src` (direkte, bereits autorisierte URL) oder `doc.id` (lädt /api/documents/preview/{id}). */
export const DocPreviewModal = ({ doc, token, src, onClose, onDownload, extraActions = null }) => {
  const [blobUrl, setBlobUrl] = useState(null);
  const [state, setState] = useState('loading');
  const frameRef = useRef(null);
  const ext = (doc?.file_type || doc?.name?.split('.').pop() || '').toLowerCase();
  const isImage = ['png', 'jpg', 'jpeg', 'webp', 'gif'].includes(ext);
  const isPdf = ext === 'pdf';
  const canEmbedPdf = !(typeof navigator !== 'undefined' && navigator.pdfViewerEnabled === false);

  const print = () => {
    try {
      frameRef.current.contentWindow.focus();
      frameRef.current.contentWindow.print();
    } catch (e) {
      window.open(blobUrl, '_blank', 'noopener');
    }
  };

  useEffect(() => {
    if (!doc) return undefined;
    if (src) { setBlobUrl(src); setState('ok'); return undefined; }
    let url = null;
    setState('loading'); setBlobUrl(null);
    fetch(`${API}/api/documents/preview/${doc.id}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => { if (!res.ok) throw new Error(); return res.blob(); })
      .then(blob => { url = URL.createObjectURL(blob); setBlobUrl(url); setState('ok'); })
      .catch(() => setState('error'));
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [doc?.id, token, src]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!doc) return null;
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[70] flex items-center justify-center p-4" onClick={onClose} data-testid="doc-preview-overlay">
      <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-xl w-full max-w-5xl h-[85vh] shadow-2xl flex flex-col overflow-hidden" data-testid="doc-preview-modal"
        onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <FileText className="w-5 h-5 text-brand-gold flex-shrink-0" />
            <div className="min-w-0">
              <h3 className="font-semibold text-brand-navy text-sm truncate" data-testid="doc-preview-title">{doc.name}</h3>
              <p className="text-xs text-gray-400">{[doc.category, doc.created_at ? new Date(doc.created_at).toLocaleDateString('de-DE') : null, doc.file_size ? `${(doc.file_size / 1024 / 1024).toFixed(1)} MB` : null].filter(Boolean).join(' · ')}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {extraActions}
            {isPdf && state === 'ok' && canEmbedPdf && (
              <button onClick={print} data-testid="doc-preview-print"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-brand-gold text-brand-navy font-semibold rounded-lg hover:bg-brand-gold-light transition-colors">
                <Printer className="w-3.5 h-3.5" /> Drucken
              </button>
            )}
            <button onClick={() => onDownload(doc)} data-testid="doc-preview-download"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-brand-navy text-white rounded-lg hover:bg-brand-navy/90 transition-colors">
              <Download className="w-3.5 h-3.5" /> Download
            </button>
            <button onClick={onClose} data-testid="doc-preview-close" aria-label="Vorschau schließen"
              className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        <div className="flex-1 bg-gray-100 overflow-auto">
          {state === 'loading' ? (
            <div className="flex items-center justify-center h-full"><Loader2 className="w-8 h-8 text-brand-gold animate-spin" /></div>
          ) : state === 'error' ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-500"><AlertCircle className="w-10 h-10 mb-2 text-red-400" /><p className="text-sm">Vorschau konnte nicht geladen werden.</p></div>
          ) : isImage ? (
            <div className="flex items-center justify-center h-full p-4"><img src={blobUrl} alt={doc.name} onError={() => setState('error')} className="max-w-full max-h-full object-contain rounded-lg shadow" /></div>
          ) : isPdf && !canEmbedPdf ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-500 gap-3 p-6 text-center">
              <FileText className="w-10 h-10" />
              <p className="text-sm">Dieser Browser zeigt PDFs nicht eingebettet an.</p>
              <button onClick={() => window.open(blobUrl, '_blank', 'noopener')} data-testid="doc-preview-open-tab"
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-brand-navy text-white rounded-lg">PDF in neuem Tab öffnen</button>
            </div>
          ) : isPdf ? (
            <iframe ref={frameRef} src={blobUrl} title={doc.name} className="w-full h-full border-0" data-testid="doc-preview-frame" />
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-gray-500 gap-2">
              <FileText className="w-10 h-10" />
              <p className="text-sm">Für dieses Dateiformat ist keine Vorschau verfügbar.</p>
              <button onClick={() => onDownload(doc)} className="text-xs text-brand-navy underline">Datei herunterladen</button>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default DocPreviewModal;
